import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from '@app/store';
import {
  createPhaseForm,
  updatePhaseForm,
  fetchPhaseFormById,
  setCurrentPhaseForm,
} from "./phaseFormSlice";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  SwapVerticalCircle as SwapVerticalCircleIcon,
} from "@mui/icons-material";
import {
  PhaseFormOutputDTO,
  PhaseFormFieldData,
  PhaseFormFieldType,
  PhaseFormFieldTypeLabels,
  PhaseFormTypeLabels,
  PhaseFormType,
  PhaseFormFormat,
  PhaseFormFormatLabels,
} from './types';
import { ApiError } from '@/types/api';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { phaseFormMapper } from "./phaseFormMapper";
import { useNotification } from '@hooks/useNotification';

import { fetchProcessRoles } from "../processRole/processRoleSlice";
import { fetchAssetTypes } from "../assetType/assetTypeSlice";
import { fetchAppointments } from '@features/appointment/appointmentSlice';
import { Appointment } from '@features/appointment/types';

import styled from "styled-components";
import { DeleteButton } from "@components/ui/DeleteButton";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Delete } from "lucide-react";

const DropZone = styled.div`
  height: ${(props) => (props.hidden ? "0px" : "20px")};
  ${(props) => (props.hidden ? "padding: 0px;" : "")};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%; /* Ocupa toda a largura disponível */
`;

const LinhaSuperior = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const BoxContainer = styled.div`
  flex-shrink: 0; /* Não deixa o Box crescer */
  margin-right: 8px; /* Espaçamento entre os itens */
`;

const FormContainer = styled.div`
  flex-grow: 1; /* Ocupa todo o espaço restante */
`;

const DropZoneContainer = styled.div`
  width: 100%; /* Ocupa toda a largura */
`;

const PhaseFormForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.phaseForm
  );
  console.log('loading', loading)
  const [loading_data, setLoadingData] = useState(false);
  const [formData, setFormData] = useState<Partial<PhaseFormOutputDTO>>({});
  const [localFields, setLocalFields] = useState<PhaseFormFieldData[]>([]);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);
  const dependenciesLoadedRef = useRef(false);

  const [dragged, setDragged] = useState<number | null>(null); // storing the dragged item as an index
  const [mouse, setMouse] = useState<[number, number]>([0, 0]);

  const mouse_ref: any = useRef();
  // const [dropZone, setDropZone] = useState(0);

  const dropZoneCurrent: any = useRef();

  const [hasAppointment, setHasAppointment] = useState(false);
  const [appointmentId, setAppointmentId] = useState<string | undefined>(undefined);

  // Buscar as configurações de agendamento disponíveis
  const appointments = useSelector((state: RootState) => state.appointment?.items || []);
  const appointmentsLoading = useSelector((state: RootState) => state.appointment?.loading || false);
  const mouseDown: any = useRef();
  // drop item
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      mouseDown.current = false;

      if (dragged !== null) {
        e.preventDefault();
        setDragged(null);

        if (
          dragged < 0 ||
          dragged >= localFields.length ||
          dropZoneCurrent.current < 0 ||
          dropZoneCurrent.current >= localFields.length
        ) {
          console.error("Índices inválidos.");
          return;
        }

        const novolocalFields = [...localFields]; // Cria uma cópia para evitar mutação
        const [itemRemovido] = novolocalFields.splice(dragged, 1); // Remove o item da posição B
        novolocalFields.splice(
          dropZoneCurrent.current + (dragged <= dropZoneCurrent.current ? 0 : 1),
          0,
          itemRemovido
        ); // Insere o item na posição C

        setLocalFields(novolocalFields);
      }
    };

    // document.addEventListener("mouseup", handler);
    // document.addEventListener("mousedown", (e) => {
    //   mouseDown.current = true;
    // });

    // return () => document.removeEventListener("mouseup", handler);
  });

  // get closest drop zone
  const drag_function = () => {
    if (dragged !== null) {
      // get all drop-zones
      const elements = Array.from(document.getElementsByClassName("drop-zone"));
      // get all drop-zones' y-axis position
      const positions = elements.map((e) => [
        e.id,
        e.getBoundingClientRect().top,
      ]);
      // get the difference with the mouse's y position
      const absDifferences = positions.map((v: any) =>
        Math.abs(v[1] - mouse_ref.current[1])
      );

      // get the item closest to the mouse
      let result = absDifferences.indexOf(Math.min(...absDifferences));
      result += dragged <= result ? 1 : 0;
      console.log(mouse_ref.current, dragged, result, positions);
      dropZoneCurrent.current = result;
      // setDropZone(result);
    }
  }

  // get mouse coordinates
  useEffect(() => {
    // const handler = (e: MouseEvent) => {
    //   if (mouseDown.current == true) {
    //     mouse_ref.current = [e.x, e.y];
    //     drag_function();
    //     // setMouse([e.x, e.y]);
    //   }
    // };

    // document.addEventListener("mousemove", handler);

    // return () => document.removeEventListener("mousemove", handler);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 PhaseFormForm - Iniciando carregamento de dados");
        isLoadingRef.current = true;

        if (id) {
          console.log("📡 PhaseFormForm - Carregando formulário:", id);
          const resultAction = await dispatch(fetchPhaseFormById(id));

          if (fetchPhaseFormById.fulfilled.match(resultAction)) {
            console.log("✅ PhaseFormForm - Formulário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar formulário");
          }
        }

        // Carrega dependências apenas uma vez
        if (!dependenciesLoadedRef.current) {
          console.log("📡 PhaseFormForm - Carregando dependências");
          const [rolesResult, typesResult] = await Promise.all([
            dispatch(fetchProcessRoles({})),
            dispatch(fetchAssetTypes({})),
          ]);

          if (rolesResult && typesResult) {
            console.log("✅ PhaseFormForm - Dependências carregadas");
            dependenciesLoadedRef.current = true;
          } else {
            throw new Error("Erro ao carregar dependências");
          }
        }
      } catch (error) {
        console.error("❌ PhaseFormForm - Erro ao carregar dados:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);

      }
    };

    loadData();

    return () => {
      dispatch(setCurrentPhaseForm(null));
    };
  }, [id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 PhaseFormForm - Atualizando dados do formulário:",
        currentItem
      );

      setFormData({
        name: currentItem.name,
        description: currentItem.description,
        type: currentItem.type,
        format: currentItem.format,
        title: currentItem.title,
        subtitle: currentItem.subtitle,
        short_description: currentItem.short_description,
      });

      if (currentItem.formFields) {
        setLocalFields(currentItem.formFields);
      }
    }
  }, [currentItem]);

  // Adicionar carregamento de agendamentos
  useEffect(() => {
    const loadAppointments = async () => {
      if (!dependenciesLoadedRef.current) {
        try {
          await dispatch(fetchAppointments({} as Record<string, string>));
          console.log("✅ PhaseFormForm - Agendamentos carregados");
        } catch (error) {
          console.error("❌ PhaseFormForm - Erro ao carregar agendamentos:", error);
        }
      }
    };

    loadAppointments();
  }, []);

  // Carregar dados de agendamento quando estiver editando
  useEffect(() => {
    if (currentItem && initialLoadDoneRef.current) {
      setHasAppointment(!!currentItem.hasAppointment);
      setAppointmentId(currentItem.appointmentId);
    }
  }, [currentItem, initialLoadDoneRef.current]);

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "title",
      label: "Título",
      type: "text",
      validationError: formError?.validationErrors?.title?.message,
    },
    {
      name: "subtitle",
      label: "Subtítulo",
      type: "text",
      validationError: formError?.validationErrors?.subtitle?.message,
    },
    {
      name: "description",
      label: "Descrição",
      type: "textarea",
      rows: 3,
      validationError: formError?.validationErrors?.description?.message,
    },
    {
      name: "short_description",
      label: "Descrição Curta",
      type: "textarea",
      rows: 2,
      validationError: formError?.validationErrors?.short_description?.message,
    },
    {
      name: "type",
      label: "Tipo",
      type: "select",
      required: true,
      options: Object.entries(PhaseFormTypeLabels).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      name: "format",
      label: "Modo de Exibição",
      type: "select",
      required: true,
      options: Object.entries(PhaseFormFormatLabels).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      name: "tag",
      label: "Tag",
      type: "select",
      placeholder: "Selecione uma tag para identificação do campo",
      options: [
        { value: "", label: "Nenhuma tag" },
        { value: "PESSOA-CLIENTE", label: "Pessoa Principal" },
        { value: "ID-CLIENTE", label: "Identificador do Cliente" },
        { value: "NOME-CLIENTE", label: "Nome do Cliente" },
        { value: "EMAIL-CLIENTE", label: "Email do Cliente" },
        { value: "CELULAR-CLIENTE", label: "Celular do Cliente" },
      ],
    },
  ];

  const handleFieldUpdate = (index: number, updatedField: PhaseFormFieldData) => {
    console.log("Updating field at index:", index, updatedField); // Debug
    const newFields = [...localFields];
    newFields[index] = updatedField;
    setLocalFields(newFields);
  };

  // O backend devolve as validacoes com a chave "formFields.<indice>.<campo>". Sem
  // traduzir o indice para "Campo N" o usuario le "O campo Nome e obrigatorio para cada
  // campo do formulario" sem descobrir qual dos campos do formulario reprovou.
  const descreverErro = (error: ApiError): string => {
    const entradas = Object.entries(error.validationErrors || {});
    if (entradas.length === 0) return error.message || "Erro ao salvar dados";

    return entradas
      .map(([chave, detalhe]) => {
        const posicao = chave.match(/^formFields\.(\d+)\./);
        return posicao
          ? `Campo ${Number(posicao[1]) + 1}: ${detalhe.message}`
          : detalhe.message;
      })
      .join(" | ");
  };

  const handleSubmit = async (data: Partial<PhaseFormOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      const finalData = {
        ...data,
        formFields: localFields,
        hasAppointment,
        appointmentId: hasAppointment ? appointmentId : undefined,
      };

      const mappedData = phaseFormMapper.toDTO(finalData);
      console.log("📤 PhaseFormForm - Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updatePhaseForm({ id, data: mappedData }))
        : await dispatch(createPhaseForm(mappedData));

      if (
        updatePhaseForm.rejected.match(resultAction) ||
        createPhaseForm.rejected.match(resultAction)
      ) {
        // O payload pode vir vazio (falha de rede, thunk sem rejectWithValue). Ler
        // `.message` direto de undefined estourava um TypeError que caia no catch de
        // baixo e virava "Erro inesperado", escondendo o motivo real do backend.
        const error = (resultAction.payload ?? {
          code: "UNKNOWN_ERROR",
          message: "Nao foi possivel salvar. Verifique a conexao e tente de novo.",
          statusCode: 0,
        }) as ApiError;
        setFormError(error);
        notification.showError(descreverErro(error));
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/phase-forms");
    } catch (error) {
      console.error("❌ PhaseFormForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  const handleAddField = () => {
    setLocalFields((prev) => [
      ...prev,
      {
        _id: "",
        name: "",
        multiline: false,
        type: PhaseFormFieldType.TEXT,
        text: "",
        description: "",
        videoUrl: "",
        processRole: undefined,
        assetType: undefined,
        required: true,
      },
    ]);
  };

  const handleFieldRemove = (index: number) => {
    setLocalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (isInitialLoad && loading) {
    return <FormSkeleton fields={2} additionalFields={true} />;
  }


  const onDragEnd = (result:any) => {
    console.log('result', result);
    if (!result.destination) {
      return;
    }

     const novolocalFields = [...localFields]; // Cria uma cópia para evitar mutação
        const [itemRemovido] = novolocalFields.splice(result.source.index, 1); // Remove o item da posição B
        novolocalFields.splice(
          result.destination.index, 0, itemRemovido
        ); // Insere o item na posição C

        setLocalFields(novolocalFields);

    // const selectedModulos = [...formData.selectedModulos];
    // const [reorderedItem] = selectedModulos.splice(result.source.index, 1);
    // selectedModulos.splice(result.destination.index, 0, reorderedItem);

    // setFormData((prevState) => ({
    //   ...prevState,
    //   selectedModulos,
    // }));
  };
  console.log('localfields', localFields)
  return (
    <Box sx={{ p: 0 }}>
      {/* <Typography variant="h5" gutterBottom>
        {id ? "Editar Formulário de Fase" : "Novo Formulário de Fase"}
      </Typography> */}
      {isInitialLoad == false ? <>
        <DataForm
          data={formData}
          fields={fields}
          onSubmit={handleSubmit}
          basePath="phase-forms"
          title={id ? "Editar Formulário de Fase" : "Novo Formulário de Fase"}
          error={formError}
          onClearError={() => setFormError(null)}
          disabled={isSubmitting}
          children={() => <>
            <Box sx={{ mt: 4 }}>
              <p style={{ fontSize: 18 }}><b>Configuração de Agendamento</b></p>


              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <input
                  type="checkbox"
                  id="hasAppointment"
                  checked={hasAppointment}
                  onChange={(e) => setHasAppointment(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor="hasAppointment">
                  Este formulário permite agendamento
                </label>
              </Box>

              {hasAppointment && (
                <Box sx={{ mt: 2 }}>
                  {/* <p style={{ fontSize: 18 }}><b>Configuração de Agendamento</b></p> */}


                  {appointmentsLoading ? (
                    <Typography>Carregando configurações...</Typography>
                  ) : (
                    <select
                      value={appointmentId || ''}
                      onChange={(e) => setAppointmentId(e.target.value || undefined)}
                      style={{
                        padding: '8px',
                        width: '100%',
                        // maxWidth: '400px',
                        borderRadius: '4px',
                        border: '1px solid #ccc'
                      }}
                      className={`w-full h-12 border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
                    >
                      <option value="">Selecione uma configuração</option>
                      {appointments.map((appointment: Appointment) => (
                        <option key={appointment._id} value={appointment._id}>
                          {appointment.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {hasAppointment && !appointmentId && (
                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                      Selecione uma configuração de agendamento
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <p style={{ fontSize: 18 }}><b>Campos do Formulário</b></p>

                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    handleAddField();
                    setExpandedIndex(localFields.length); // Expand the newly added field
                    setTimeout(() => {
                      window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth"
                      });
                    }, 150);
                  }}
                  variant="outlined"
                  size="small"
                >
                  Adicionar Campo
                </Button>
              </Box>

              <div className="table-rep-plugin">
                <div className="table-responsive mb-0" data-pattern="priority-columns">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="conteudos">
                      {(provided: any) => (
                        <table id="tech-companies-1" className="table table-striped"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          <thead>
                            <tr>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {localFields.map((field, index)=> (
                              <Draggable key={index.toString()} draggableId={index.toString()} index={index}>
                                {(provided: any) => (
                                  <tr ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}>
                                      <td>
                          <FormFieldItem
                            field={field}
                            index={index}
                            id={"field-" + index}
                            onUpdate={handleFieldUpdate}
                            onRemove={handleFieldRemove}
                            error={formError}
                            setDragged={setDragged}
                            expanded={expandedIndex === index}
                            setExpandedIndex={setExpandedIndex}
                          />
                                      </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        </table>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>

              {/* {localFields.map((field, index) => (
                <Box key={index}>
                  {dragged === index && (
                    <div
                      style={mouse_ref.current ? {
                        left: `${mouse_ref.current[0]}px`,
                        top: `${mouse_ref.current[1]}px`,
                        position: "fixed",
                        boxShadow: "5px 5px 10px rgba(0,0,0,0.5)",
                      } : {
                        position: "fixed",
                        boxShadow: "5px 5px 10px rgba(0,0,0,0.5)",
                      }}
                    >
                      <Box>{localFields[dragged].name}</Box>
                    </div>
                  )}
                  {dragged !== index && (
                   
                  )}
                </Box>
              ))} */}
            </Box>
          </>}
        />

        {/* Seção de Agendamento */}


      </> : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90vh' }} className="w-full mt-3"><CircularProgress /></div>}

    </Box>
  );
};

interface FormFieldItemProps {
  field: PhaseFormFieldData;
  index: number;
  id: string;
  onUpdate: (index: number, data: PhaseFormFieldData) => void;
  onRemove: (index: number) => void;
  error?: any;
  setDragged?: any;
  expanded: boolean;
  setExpandedIndex: (index: number | null) => void;
}

const FormFieldItem: React.FC<FormFieldItemProps> = ({
  field,
  index,
  id,
  onUpdate,
  onRemove,
  error,
  setDragged,
  expanded,
  setExpandedIndex,
}) => {

  const assetTypes = useSelector((state: RootState) => state.assetType.items);
  const processRoles = useSelector(
    (state: RootState) => state.processRole.items
  );

  const handleExpandChange = () => {
    setExpandedIndex(expanded ? null : index); // Collapse if already expanded, otherwise expand
  };

  const handleFieldChange = (name: string, value: any) => {
    onUpdate(index, {
      ...field,
      [name]: value,
    });
  };

  const handleTypeChange = (newType: PhaseFormFieldType) => {
    console.log("handleTypeChange:", newType);

    // Preservamos os campos comuns/cabeçalho
    const commonFields = {
      _id: field._id,
      name: field.name,
      description: field.description,
      videoUrl: field.videoUrl,
      required: field.required,
      text: field.text,
    };

    // Quando o tipo muda para INVOICE ou UPLOAD, limpamos apenas campos específicos relacionados a outros tipos
    if (newType === PhaseFormFieldType.INVOICE || newType === PhaseFormFieldType.UPLOAD) {
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: undefined,
        assetType: undefined,
        options: undefined,
        // Preservamos multiline para INVOICE e UPLOAD
        multiline: field.multiline,
      });
    } else if (newType === PhaseFormFieldType.SELECT) {
      // Para SELECT, preservamos options se existirem
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: undefined,
        assetType: undefined,
        options: field.type === PhaseFormFieldType.SELECT ? field.options : undefined,
        multiline: field.multiline,
      });
    } else if (newType === PhaseFormFieldType.ROLE) {
      // Para ROLE, preservamos processRole se existir
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: field.type === PhaseFormFieldType.ROLE ? field.processRole : undefined,
        assetType: undefined,
        options: undefined,
        multiline: field.multiline,
      });
    } else if (newType === PhaseFormFieldType.ASSET) {
      // Para ASSET, preservamos assetType se existir
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: undefined,
        assetType: field.type === PhaseFormFieldType.ASSET ? field.assetType : undefined,
        options: undefined,
        multiline: field.multiline,
      });
    } else if (newType === PhaseFormFieldType.PERSON) {
      // Para PERSON, preservamos processRole se existir (usado como especialidade)
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: field.type === PhaseFormFieldType.PERSON ? field.processRole : undefined,
        assetType: undefined,
        options: undefined,
        multiline: field.multiline,
      });
    } else if (newType === PhaseFormFieldType.STRING) {
      // Para STRING, preservamos a máscara e isPassword se existirem
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: undefined,
        assetType: undefined,
        options: undefined,
        multiline: field.multiline,
        acceptFiles: field.acceptFiles,
        mask: field.type === PhaseFormFieldType.STRING ? field.mask : undefined,
        isPassword: field.type === PhaseFormFieldType.STRING ? field.isPassword : undefined,
      });
    } else {
      // Para outros tipos, mantemos o comportamento original
      onUpdate(index, {
        ...commonFields,
        type: newType,
        processRole: undefined,
        assetType: undefined,
        options: undefined,
        multiline: field.multiline,
        acceptFiles: field.acceptFiles,
      });
    }
  };

   const getConditionalFields = (type: PhaseFormFieldType): Field[] => {
    const conditionalFields: Field[] = [];

    // Se for do tipo INVOICE, adicionamos apenas o campo multiline
    if (type === PhaseFormFieldType.INVOICE) {
      conditionalFields.push({
        name: "multiline",
        label: "Permitir múltiplos arquivos (XML, ZIP, RAR)",
        type: "checkbox",
      });
      return conditionalFields;
    }
    
    // Se for do tipo UPLOAD, adicionamos apenas o campo multiline para múltiplos arquivos
    if (type === PhaseFormFieldType.UPLOAD) {
      conditionalFields.push({
        name: "multiline",
        label: "Permitir múltiplos arquivos",
        type: "checkbox",
      });
      return conditionalFields;
    }

    const assetTypeId =
      field.assetType && typeof field.assetType === "object"
        ? (field.assetType as any)._id
        : field.assetType;
    const processRoleId =
      field.processRole && typeof field.processRole === "object"
        ? (field.processRole as any)._id
        : field.processRole;

    switch (type) {
      case PhaseFormFieldType.SELECT:
        conditionalFields.push({
          name: "options",
          label: "Opções do Campo",
          type: "select-builder",
          showOptions: true,
          selectOptions:
            field.options?.map((opt, index) => ({
              key: opt.key || "",
              value: index.toString(),
              hidden: true
            })) || [],
          onSelectOptionsChange: (newOptions) => {
            handleFieldChange(
              "options",
              newOptions.map((opt) => ({
                key: opt.key || "",
                value: opt.value,
              }))
            );
          },
        });
        break;

      case PhaseFormFieldType.ROLE:
        conditionalFields.push({
          name: "processRole",
          label: "Papel do Processo",
          type: "select",
          required: true,
          options: processRoles.map((role) => ({
            value: role._id ?? "",
            label: role.name ?? "",
          })),
          value: processRoleId,
        });
        break;

      case PhaseFormFieldType.ASSET:
        conditionalFields.push({
          name: "assetType",
          label: "Tipo de Bem",
          type: "select",
          required: true,
          options: assetTypes.map((type) => ({
            value: type._id ?? "",
            label: type.name ?? "",
          })),
          value: assetTypeId,
        });
        break;
      case PhaseFormFieldType.PERSON:
        conditionalFields.push({
          name: "processRole",
          label: "Especialidade Requerida",
          type: "select",
          required: true,
          options: [
            { value: "", label: "Nenhuma Especialidade" }, // Opção adicionada
            ...processRoles.map((role) => ({
              value: role._id ?? "",
              label: role.name ?? "",
            })),
          ],
          value: processRoleId,
        });
        // Campo tag para PERSON
        conditionalFields.push({
          name: "tag",
          label: "Tag",
          type: "select",
          placeholder: "Selecione uma tag para identificação do campo",
          options: [
            { value: "", label: "Nenhuma tag" },
            { value: "PESSOA-CLIENTE", label: "Pessoa Principal" },
          ],
        });
        break;
        
      case PhaseFormFieldType.STRING:
        conditionalFields.push({
          name: "mask",
          label: "Máscara do Campo",
          type: "text",
          placeholder: "Ex: ###.###.###-##, (##) #####-####, AAA-####",
          helperText: "Use # para dígitos, A para letras maiúsculas, a para minúsculas, X para alfanuméricos"
        });
        conditionalFields.push({
          name: "isPassword",
          label: "Campo de Senha",
          type: "checkbox",
          helperText: "Marque esta opção se o campo deve ser tratado como senha (texto oculto)"
        });
        // Campo tag para STRING (Texto Curto)
        conditionalFields.push({
          name: "tag",
          label: "Tag",
          type: "select",
          placeholder: "Selecione uma tag para identificação do campo",
          options: [
            { value: "", label: "Nenhuma tag" },
            { value: "ID-CLIENTE", label: "Identificador do Cliente" },
            { value: "NOME-CLIENTE", label: "Nome do Cliente" },
            { value: "EMAIL-CLIENTE", label: "Email do Cliente" },
            { value: "CELULAR-CLIENTE", label: "Celular do Cliente" },
          ],
        });
        break;
    }
    if (type == PhaseFormFieldType.SELECT) {
      conditionalFields.push({
        name: "multiline",
        label: "Múltiplas Respostas",
        type: "checkbox",
      });
    } else if (
      type == PhaseFormFieldType.PERSON ||
      type == PhaseFormFieldType.ASSET ||
      type == PhaseFormFieldType.ROLE
    ) {
      conditionalFields.push({
        name: "multiline",
        label: "Múltiplas Linhas",
        type: "checkbox",
      });
    }
    
    // Campo de aceitar arquivos adicionado para todos os tipos que não são INVOICE
    // Como já verificamos no início se o tipo é INVOICE e retornamos campos vazios,
    // todos os campos abaixo não serão para o tipo INVOICE
    conditionalFields.push({
      name: "acceptFiles",
      label: "Aceita upload de arquivos",
      type: "checkbox",
    });

    return conditionalFields;
  };

  // Determine quais campos básicos devem ser exibidos com base no tipo
  let baseFields: Field[] = [
    {
      name: "name",
      label: "Nome do Campo",
      type: "text",
      required: true,
      validationError:
        error?.validationErrors?.[`formFields.${index}.name`]?.message,
    },
    {
      name: "description",
      label: "Descrição",
      type: "textarea",
      rows: 3,
    },
    {
      name: "type",
      label: "Tipo do Campo",
      type: "select",
      required: true,
      options: Object.entries(PhaseFormFieldTypeLabels).map(
        ([value, label]) => ({
          value,
          label,
        })
      ),
      value: field.type,
      onChange: (value) => handleTypeChange(value as PhaseFormFieldType),
    },
    {
      name: "required",
      label: "Campo Obrigatório",
      type: "checkbox",
      value: field.required || false,
    },
    {
      name: "videoUrl",
      label: "URL do Vídeo",
      type: "text",
      placeholder: "https://www.youtube.com/watch?v=...",
      pattern: "https?://.*",
      validationError:
        error?.validationErrors?.[`formFields.${index}.videoUrl`]?.message,
    }
  ]



  // Para campos do tipo INVOICE ou UPLOAD, não precisamos adicionar campos extras
  if (field.type === PhaseFormFieldType.INVOICE || field.type === PhaseFormFieldType.UPLOAD) {
    // Não adicionamos campos extras para INVOICE ou UPLOAD
  } else {
    // Para outros tipos, não precisamos adicionar campos extras pois já estão incluídos no baseFields
  }

  return (
    <Accordion id={id} expanded={expanded} onChange={handleExpandChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BoxContainer>
          <Box
            key={"key" + index}
            onMouseDown={(e) => {
              e.preventDefault();
              setDragged(index);
              setExpandedIndex(null)
            }}
          >
            <SwapVerticalCircleIcon /> {index+1})
          </Box>
        </BoxContainer>

        <Typography>
          {field.name || "Campo sem nome"}
          {field.type === PhaseFormFieldType.INVOICE && " (Notas Fiscais)"}
          {field.type === PhaseFormFieldType.UPLOAD && " (Upload de Arquivos)"}
        </Typography>

        <DeleteButton
          sx={{ marginRight: 2 }}
          messageText="Confirma a remoção do item?"
          onRemove={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
        />
      </AccordionSummary>
      <AccordionDetails>
        <DataForm
          data={field}
          isForm
          fields={baseFields.concat(getConditionalFields(field.type))}
          onSubmit={async () => Promise.resolve()}
          basePath=""
          title=""
          hideButtons
          onChange={(name, value) => handleFieldChange(name, value)}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default PhaseFormForm;
